<script lang="ts">
interface Props {
    modelValue: boolean
    shipmentId?: uuid
}
</script>

<script setup lang="ts">
import type { uuid } from '@/types/general'

import { watch, computed, toRef, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import Datepicker from '@vuepic/vue-datepicker'
import dayjs from 'dayjs'

import { useAuthStore } from '@/stores/auth-store'
import { ShipmentType } from '@/types/delivery-management'
import { useShipmentForm } from '@/hooks/delivery-management/use-shipment-form'

import ColliPickerModal from '@/components/delivery-management/ColliPickerModal.vue'
import MyCheckbox from '@/components/my-components/form/MyCheckbox.vue'
import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import MyErrorMessage from '@/components/my-components/form/MyErrorMessage.vue'
import MyInputLabel from '@/components/my-components/form/MyInputLabel.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import LoaderWrapper from '@/components/loaders/LoaderWrapper.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import MyModal from '@/components/my-components/MyModal.vue'
import MyForm from '@/components/my-components/form/MyForm.vue'
import MyInput from '@/components/my-components/form/MyInput.vue'
import ColliCard from '@/components/delivery-management/ColliCard.vue'

const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'close'): void
    (e: 'saved'): void
}>()

const authStore = useAuthStore()
const { t } = useI18n()
const title = computed(() => {
    if (props.shipmentId) {
        return t('updateEntity', { entity: t('shipment') })
    } else {
        return t('createEntity', { entity: t('shipment') })
    }
})
const informationToggle = ref(false)
const {
    data,
    errors,
    loading,
    shipment,
    colliPickerModalOpen,
    receivers,
    shippers,
    receiverLocations,
    shipperLocations,
    hauliers,
    haulierLocations,
    onSubmit,
    onModalOpen,
    removeColli,
    companyLocations,
    companyIsShipper,
    receiverCompanyChanged,
    senderLocationChanged,
    shipperCompanyChanged,
    haulierCompanyChanged,
    categories,
} = useShipmentForm({
    shipmentId: toRef(() => props.shipmentId),
    onSave: () => {
        emit('close')
        emit('saved')
    },
    isConsignmentNote: false,
})

function handleableBeforeOpened() {
    if (!data.plannedAt || data.handleableBefore !== null) return

    data.handleableBefore = dayjs(data.plannedAt).endOf('day').toISOString()
}

function handleableAfterOpened() {
    if (!data.plannedAt || data.handleableAfter !== null) return

    data.handleableAfter = dayjs(data.plannedAt).startOf('day').toISOString()
}

watch(
    () => props.modelValue,
    () => {
        if (props.modelValue) onModalOpen()
    },
)
</script>

<template>
    <MyModal :value="props.modelValue" :max-width="850" @close="emit('close')">
        <LoaderWrapper :visible="loading" class="rounded-xl" />
        <template #title>
            {{ title }}
        </template>
        <MyForm :errors="errors" @submit.prevent="onSubmit">
            <div class="flex">
                <div class="flex w-full flex-col pr-3">
                    <div class="flex items-end space-x-3">
                        <div class="flex w-1/2 flex-col space-y-3">
                            <div>
                                <MyInputLabel v-text="t('sender')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="
                                        !companyIsShipper
                                            ? authStore.company.name
                                            : shipment?.sendingCompany.name
                                    "
                                />
                            </div>
                            <MySelect
                                v-if="!companyIsShipper"
                                v-model="data.receivingCompanyId"
                                :options="receivers"
                                :label="t('receiver')"
                                searchable
                                @change="receiverCompanyChanged"
                            />
                            <div v-else>
                                <MyInputLabel v-text="t('receiver')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="shipment?.receivingCompany.name"
                                />
                            </div>
                        </div>
                        <div class="flex w-1/2 flex-col space-y-3">
                            <MySelect
                                v-if="!companyIsShipper"
                                v-model="data.sendingLocationId"
                                :options="companyLocations"
                                :label="t('sendingLocation')"
                                name="sendingLocationId"
                                searchable
                                @change="senderLocationChanged"
                            />
                            <div v-else>
                                <MyInputLabel v-text="t('sendingLocation')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="shipment?.sendingLocation.name"
                                />
                            </div>

                            <MySelect
                                v-if="!companyIsShipper"
                                v-model="data.receivingLocationId"
                                :options="receiverLocations"
                                :label="t('receivingLocation')"
                                name="receivingLocationId"
                                searchable
                            />

                            <div v-else>
                                <MyInputLabel v-text="t('receivingLocation')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="shipment?.receivingLocation.name"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="!m-0 flex w-full space-x-3">
                        <div class="mt-3 flex w-1/2 flex-col">
                            <MySelect
                                v-if="!companyIsShipper"
                                v-model="data.shipperCompanyId"
                                name="shipper"
                                group-class="w-full"
                                :label="t('shipper')"
                                searchable
                                clear-button
                                :options="shippers"
                                @change="shipperCompanyChanged"
                            />
                            <div v-else>
                                <MyInputLabel v-text="t('shipper')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="shipment?.shipperCompany?.name"
                                />
                            </div>
                        </div>
                        <div class="mt-3 flex w-1/2 flex-col">
                            <MySelect
                                v-if="!companyIsShipper"
                                v-model="data.shipperLocationId"
                                :options="shipperLocations"
                                :label="t('shipperLocation')"
                                searchable
                            />
                            <div v-else>
                                <MyInputLabel v-text="t('shipperLocation')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="shipment?.shipperLocation?.name"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="!m-0 flex w-full space-x-3">
                        <div class="mt-3 flex w-1/2 flex-col">
                            <MySelect
                                v-if="!companyIsShipper"
                                v-model="data.haulierCompanyId"
                                name="shipper"
                                group-class="w-full"
                                :label="t('haulier')"
                                searchable
                                clear-button
                                :options="hauliers"
                                @change="haulierCompanyChanged"
                            />
                            <div v-else>
                                <MyInputLabel v-text="t('haulier')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="shipment?.haulierCompany?.name"
                                />
                            </div>
                        </div>
                        <div class="mt-3 flex w-1/2 flex-col">
                            <MySelect
                                v-if="!companyIsShipper"
                                v-model="data.haulierLocationId"
                                :options="haulierLocations"
                                :label="t('haulierLocation')"
                                searchable
                            />
                            <div v-else>
                                <MyInputLabel v-text="t('haulierLocation')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="shipment?.haulierLocation?.name"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="!m-0 flex w-full space-x-3 space-y-3">
                        <div class="flex w-1/2 pt-3">
                            <MyRadioButtonGroup
                                v-model="data.type"
                                class="w-full"
                                name="type"
                                :label="t('type')"
                            >
                                <MyRadioButton
                                    :label="t('delivery')"
                                    :value="ShipmentType.Delivery"
                                />
                                <MyRadioButton :label="t('return')" :value="ShipmentType.Return" />
                                <MyRadioButton :label="t('pickup')" :value="ShipmentType.Pickup" />
                            </MyRadioButtonGroup>
                        </div>

                        <div class="mt-3 w-1/2">
                            <MyInputLabel
                                v-text="
                                    data.type === ShipmentType.Delivery
                                        ? t('expectedDeliveryDate')
                                        : t('expectedPickupDate')
                                "
                            />
                            <Datepicker
                                v-model="data.plannedAt"
                                class="input-field w-full rounded-xl border border-stone-300"
                                name="plannedAt"
                                close-on-scroll
                                :enable-time-picker="false"
                                auto-apply
                                clearable
                                auto-position
                                position="left"
                                format="yyyy-MM-dd"
                                :transitions="false"
                                :placeholder="t('selectDate')"
                            />
                            <MyErrorMessage input-name="plannedAt" :label="t('date')" />
                        </div>

                        <div class="mt-3 w-1/2">
                            <MySelect
                                v-if="!companyIsShipper"
                                v-model="data.categoryId"
                                name="categoryId"
                                group-class="w-full"
                                :label="t('category')"
                                searchable
                                clear-button
                                :options="categories"
                            />

                            <div v-else>
                                <MyInputLabel v-text="t('category')" />
                                <h4
                                    class="mt-0 flex h-10 cursor-default items-center rounded-xl border border-primary-200 pl-2 text-sm font-semibold text-stone-600 dark:border-dark-400 dark:text-primary-200"
                                    v-text="shipment?.category?.name"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="!mx-0 mt-2 flex flex-col mb-2">
                        <div
                            class="mt-2 ml-0.5 flex cursor-pointer justify-between"
                            @click="informationToggle = !informationToggle"
                        >
                            <h5
                                class="mb-2 select-none text-base"
                                v-text="t('additionalInformation')"
                            />

                            <mdi:chevron-down
                                class="ml-1 transition duration-300"
                                :class="{ 'rotate-180': informationToggle }"
                            />
                        </div>

                        <hr class="mb-2" />

                        <div v-if="informationToggle" class="space-y-2">
                            <div class="flex space-x-3">
                                <MyInput
                                    v-model="data.shipmentNumber"
                                    name="shipmentNumber"
                                    type="text"
                                    :label="t('shipmentNumber')"
                                />
                                <MyInput
                                    v-model="data.references"
                                    name="references"
                                    type="text"
                                    :label="t('references')"
                                />
                            </div>

                            <div class="flex space-x-3">
                                <MyInput
                                    v-model="data.name"
                                    name="name"
                                    type="text"
                                    :label="t('name')"
                                />
                                <MyInput
                                    v-model="data.soldTo"
                                    name="soldTo"
                                    type="text"
                                    :label="t('soldTo')"
                                />
                                <MyInput
                                    v-model="data.shipTo"
                                    name="shipTo"
                                    type="text"
                                    :label="t('shipTo')"
                                />
                            </div>

                            <div class="flex space-x-3 justify-evenly">
                                <div class="flex w-1/2 flex-col">
                                    <MyInputLabel
                                        v-text="
                                            data.type === ShipmentType.Delivery
                                                ? t('deliverAfter')
                                                : t('pickupAfter')
                                        "
                                    />

                                    <Datepicker
                                        v-model="data.handleableAfter"
                                        class="input-field w-full rounded-xl border border-stone-300"
                                        name="handleableAfter"
                                        close-on-scroll
                                        enable-time-picker
                                        auto-apply
                                        clearable
                                        auto-position
                                        position="left"
                                        format="yyyy-MM-dd HH:mm"
                                        :transitions="false"
                                        :placeholder="t('selectDate')"
                                        @open="handleableAfterOpened"
                                    />
                                </div>

                                <div class="flex w-1/2 flex-col">
                                    <MyInputLabel
                                        v-text="
                                            data.type === ShipmentType.Delivery
                                                ? t('deliverBefore')
                                                : t('pickupBefore')
                                        "
                                    />

                                    <Datepicker
                                        v-model="data.handleableBefore"
                                        class="input-field w-full rounded-xl border border-stone-300"
                                        name="handleableBefore"
                                        close-on-scroll
                                        enable-time-picker
                                        auto-apply
                                        clearable
                                        auto-position
                                        position="left"
                                        format="yyyy-MM-dd HH:mm"
                                        :transitions="false"
                                        :placeholder="t('selectDate')"
                                        @open="handleableBeforeOpened"
                                    />
                                </div>
                            </div>
                            <MyErrorMessage
                                input-name="handleableBefore"
                                :label="
                                    data.type === ShipmentType.Delivery
                                        ? t('deliverBefore')
                                        : t('pickupBefore')
                                "
                                class="!mt-3 !p-0"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <MyCheckbox
                v-model="data.palletExchange"
                name="palletExchange"
                :label="t('palletExchange')"
            />

            <div class="mt-2 flex justify-end">
                <MyButton
                    class="h-10 w-3/12"
                    plain
                    type="button"
                    scheme="primary"
                    @click="colliPickerModalOpen = true"
                >
                    <mdi:plus-thick class="mr-1" />
                    {{ t('addCollis') }}
                </MyButton>
            </div>

            <ColliPickerModal
                v-model="colliPickerModalOpen"
                :existing-collis="data.collis"
                @close="colliPickerModalOpen = false"
                @save-collis="data.collis = $event"
            />

            <div v-if="data.collis && data.collis.length > 0" class="mt-4 space-y-3">
                <ColliCard v-for="entry in data.collis" :key="entry.colli.id" :colli="entry.colli">
                    <template #suffix>
                        <div class="flex items-center justify-end w-[20%] ml-auto">
                            <div class="flex flex-col items-center self-start mr-4">
                                <span class="text-primary-300" v-text="t('amount')" />
                                <MyInput
                                    v-model="entry.amount"
                                    class="h-9"
                                    name="amount"
                                    type="number"
                                    min="1"
                                    required
                                />
                            </div>

                            <mdi:trash-can
                                v-tooltip="t('remove')"
                                class="h-5 w-5 cursor-pointer text-red-400 flex-shrink-0"
                                @click="removeColli(entry.colli)"
                            />
                        </div>
                    </template>
                </ColliCard>
            </div>

            <div
                v-else
                class="mt-2 flex w-full items-center justify-center rounded-xl bg-primary-50 p-5 dark:bg-dark-400"
            >
                <span class="dark:text-primary-200">{{ t('noCollisForShipment') }}</span>
            </div>

            <div class="mt-4 flex justify-end gap-6">
                <MyButton
                    :disabled="loading"
                    scheme="primary"
                    v-text="props.shipmentId ? t('update') : t('create')"
                />
            </div>
        </MyForm>
    </MyModal>
</template>
